// AICostBadge — tiny inline badge that shows what the last generation
// for a given feature actually cost in USD. Reads from aiTokenLog's
// per-feature lastCall stamp.
//
// Usage: <AICostBadge feature="readiness" />
//
// Renders nothing if no call has been recorded yet (keeps the UI clean
// on first-launch / pre-generation states).

import React, { useEffect, useState } from "react";
import { getLastCall } from "./aiTokenLog";

function formatCostUSD(usd) {
  if (!Number.isFinite(usd) || usd <= 0) return "<$0.001";
  if (usd < 0.001) return "<$0.001";
  if (usd < 0.01)  return `$${usd.toFixed(4)}`;
  if (usd < 1)    return `$${usd.toFixed(3)}`;
  return `$${usd.toFixed(2)}`;
}

export default function AICostBadge({
  feature,
  prefix = "",          // e.g. "·" if you want a separator before the cost
  style = {},
  color = "rgba(255,255,255,0.45)",
  fontFamily = "ui-monospace, 'SF Mono', Menlo, monospace",
  fontSize = 9,
}) {
  const [last, setLast] = useState(() => getLastCall(feature));

  // Re-read on storage events so the badge updates immediately after a
  // generation completes anywhere in the app (siblings, other tabs).
  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key === "hayahTokenUsage") {
        setLast(getLastCall(feature));
      }
    };
    window.addEventListener("storage", onStorage);
    // Re-read on focus too — covers same-tab updates the storage event
    // doesn't always fire for.
    const onFocus = () => setLast(getLastCall(feature));
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
    };
  }, [feature]);

  // Also poll briefly so a fresh generation in the SAME tab/component
  // shows up even without a window focus event.
  useEffect(() => {
    const t = setInterval(() => {
      const cur = getLastCall(feature);
      if (cur && cur.ts !== last?.ts) setLast(cur);
    }, 1500);
    return () => clearInterval(t);
  }, [feature, last?.ts]);

  if (!last) return null;

  return (
    <span style={{
      fontFamily, fontSize, color,
      letterSpacing: "0.6px",
      whiteSpace: "nowrap",
      ...style,
    }}>
      {prefix ? `${prefix} ` : ""}{formatCostUSD(last.costUSD)}
    </span>
  );
}
